import { lazy, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import MeetingIcon from '@assets/meeting/meeting-header-icon.svg?react';
import { Avatar, AvatarGroup } from '@lib/core/src/components/Avatar';
import palette from '@lib/core/src/theme/palette';
import { getInitials } from '@lib/core/src/utils/string-util';
import { Tooltip } from '@mui/material';
import { RiDeleteBinLine, RiShareForwardLine } from '@remixicon/react';

import { AccountUserDto, CalendarEventDto, CalendarMeetingDto } from '@/api/generated';
import RoutesMap from '@/enums/RoutesMap';
import useMeetings from '@/hooks/useMeetings';
import useModal from '@/hooks/useModal/useModal';

import {
  DeleteButton,
  MeetingDate,
  MeetingHeaderContainer,
  MeetingHeadline,
  MeetingTitle,
  ShareButton,
  ShareDeleteContainer,
  Row,
  StartStack
} from './MeetingDataStyles';

const LazyAreYouSureModal = lazy(
  () => import('@/components/modals/AreYouSureModal/AreYouSureModal')
);

const LazyShareMeetingModal = lazy(
  () => import('@/components/modals/ShareMeetingModal/ShareMeetingModal')
);

interface MeetingHeaderProps {
  meeting: CalendarMeetingDto | undefined;
  event: CalendarEventDto | undefined;
  usersList?: AccountUserDto[];
  sharedMeeting?: boolean;
}

const MeetingHeader = ({
  meeting,
  event,
  usersList,
  sharedMeeting = false
}: MeetingHeaderProps) => {
  const { deleteMeeting } = useMeetings();
  const { showModal } = useModal();
  const navigate = useNavigate();

  const participants = usersList?.filter(
    user =>
      event?.participants?.flatMap(participant => participant.email).includes(user.email) ||
      meeting?.participants
        ?.flatMap(participant => participant.name)
        .includes(`${user.firstName} ${user.lastName}`)
  );

  const onDelete = useCallback(() => {
    const modal = showModal(LazyAreYouSureModal, {
      onClose: () => {
        modal.hide();
      },
      onSubmit: () => {
        meeting?.id &&
          deleteMeeting(meeting.id).then(() => {
            navigate(RoutesMap.Home);
          });

        modal.hide();
      },
      title: 'Delete Meeting',
      description: 'Are you sure you want to delete this meeting? this action cannot be undone.',
      actionTitle: 'Delete'
    });
  }, [showModal, deleteMeeting, meeting?.id, navigate]);

  const openShareMeeting = useCallback(() => {
    const modal = showModal(LazyShareMeetingModal, {
      onClose: () => {
        modal.hide();
      },
      onConfirm: () => {
        modal.hide();
      },
      meetingId: meeting?.id,
      calendarEventId: event?.id
    });
  }, [showModal, meeting, event]);

  return (
    <MeetingHeaderContainer>
      <MeetingHeadline>
        <MeetingIcon width="44px" height="44px" color={palette.grey[400]} />
        <StartStack>
          <Row>
            <MeetingTitle>{event ? event?.title : meeting?.title}</MeetingTitle>
            {participants && !!participants.length && (
              <AvatarGroup>
                {participants.map(participant => (
                  <Tooltip
                    title={`${participant.firstName} ${participant.lastName}`}
                    arrow
                    key={participant.id}
                  >
                    <Avatar id={participant.id} src={participant.photoUrl}>
                      {getInitials(`${participant.firstName} ${participant.lastName}`)}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Row>

          {meeting?.startTime && (
            <MeetingDate>
              {new Date(meeting.startTime).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </MeetingDate>
          )}
        </StartStack>
      </MeetingHeadline>
      {!sharedMeeting && (
        <ShareDeleteContainer>
          <ShareButton onClick={openShareMeeting}>
            <RiShareForwardLine size={16} />
            Share
          </ShareButton>
          <DeleteButton onClick={onDelete}>
            <RiDeleteBinLine size={16} />
            Delete
          </DeleteButton>
        </ShareDeleteContainer>
      )}
    </MeetingHeaderContainer>
  );
};

export default MeetingHeader;
